import { useEffect, useRef, useState } from "react";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { FileText, Plus, Pencil, Trash2, Download, X, Copy, Send, Link as LinkIcon, CheckCircle2, ChevronUp, ChevronDown } from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";
import DocPreviewPane from "../components/DocPreviewPane";

// Détecte si une chaîne contient déjà du HTML (produit par l'éditeur WYSIWYG) ou si c'est du texte brut
// hérité (avec **gras**). Dans le second cas on convertit à la volée pour que Quill puisse l'afficher.
function textToHtmlIfNeeded(body) {
  if (!body) return "";
  const trimmed = body.trim();
  if (/<\/?(p|div|span|br|strong|em|b|i|u|h[1-6]|ul|ol|li|font|a)\b/i.test(trimmed)) return body;
  // Conversion markdown minimal (ancien format) → HTML
  return (
    trimmed
      .split(/\n\n+/)
      .map((p) =>
        "<p>" +
        p
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
          .replace(/\n/g, "<br>") +
        "</p>",
      )
      .join("")
  );
}

const KIND_LABELS = { contract: "Contrat", attestation: "Attestation", avenant: "Avenant", generic: "Générique" };

const PLACEHOLDERS = [
  { code: "{{agent.first_name}}", label: "Prénom" },
  { code: "{{agent.last_name}}", label: "Nom" },
  { code: "{{agent.full_name}}", label: "Nom complet" },
  { code: "{{agent.email}}", label: "Email" },
  { code: "{{agent.phone}}", label: "Téléphone" },
  { code: "{{agent.full_address}}", label: "Adresse complète" },
  { code: "{{agent.social_security_number}}", label: "N° sécurité sociale" },
  { code: "{{agent.carte_pro_number}}", label: "Numéro carte pro" },
  { code: "{{agent.carte_pro_expires}}", label: "Expiration carte pro" },
  { code: "{{agent.contract_type}}", label: "Type contrat" },
  { code: "{{agent.contract_start_date}}", label: "Date début" },
  { code: "{{agent.contract_end_date}}", label: "Date fin (CDD)" },
  { code: "{{agent.diplomas}}", label: "Diplômes (liste)" },
  { code: "{{company.legal_name}}", label: "Raison sociale" },
  { code: "{{company.siret}}", label: "SIRET société" },
  { code: "{{company.city}}", label: "Ville société" },
  { code: "{{today}}", label: "Date du jour" },
];

function TemplateEditor({ template, onClose, onSaved }) {
  const [v, setV] = useState(() => {
    const init = template || { name: "", kind: "contract", body: "", logo_size_cm: 5.0,
      footer_extra_line: "", footer_extra_font_size: 7, footer_extra_color: "#475569",
      footer_extra_bold: false, footer_extra_italic: true };
    return { ...init, body: textToHtmlIfNeeded(init.body),
      footer_extra_line: init.footer_extra_line || "",
      footer_extra_font_size: init.footer_extra_font_size || 7,
      footer_extra_color: init.footer_extra_color || "#475569",
      footer_extra_bold: !!init.footer_extra_bold,
      footer_extra_italic: init.footer_extra_italic !== false,
    };
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [paramsCollapsed, setParamsCollapsed] = useState(false);
  const rteApi = useRef(null);

  const insertPlaceholder = (code) => {
    if (rteApi.current?.insertAtCursor) {
      rteApi.current.insertAtCursor(code);
    } else {
      setV((cur) => ({ ...cur, body: (cur.body || "") + code }));
    }
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (template?.id) await http.put(`/doc-templates/${template.id}`, v);
      else await http.post("/doc-templates", v);
      onSaved?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-3">
      <div className="bg-white rounded-md w-full max-w-[1600px] border border-slate-200 max-h-[94vh] overflow-hidden flex flex-col" onClick={(e)=>e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{template?.id ? "Modifier le modèle" : "Nouveau modèle"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 grid lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
          {/* Colonne gauche : édition — pas de scroll global, c'est l'éditeur du contenu qui scrolle en interne */}
          <div className="flex flex-col min-h-0 pr-1 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Paramètres du modèle</div>
              <button type="button" data-testid="tpl-toggle-params"
                      onClick={()=>setParamsCollapsed(c=>!c)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                {paramsCollapsed
                  ? <span className="inline-flex items-center gap-1"><ChevronDown className="h-3.5 w-3.5"/> Afficher les paramètres</span>
                  : <span className="inline-flex items-center gap-1"><ChevronUp className="h-3.5 w-3.5"/> Masquer pour agrandir l'éditeur</span>}
              </button>
            </div>
            {!paramsCollapsed && (
            <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-2 block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nom du modèle *</div>
                <input data-testid="tpl-name" className="w-full border border-slate-300 rounded px-3 py-2 text-sm" value={v.name} onChange={(e)=>setV({...v, name: e.target.value})} />
              </label>
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Type</div>
                <select className="w-full border border-slate-300 rounded px-3 py-2 text-sm" value={v.kind} onChange={(e)=>setV({...v, kind: e.target.value})}>
                  {Object.entries(KIND_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Taille du logo en en-tête (cm)</div>
              <input type="number" step="0.5" min="1" max="10" className="w-32 border border-slate-300 rounded px-3 py-2 text-sm" value={v.logo_size_cm || 5} onChange={(e)=>setV({...v, logo_size_cm: parseFloat(e.target.value)||5})} />
            </label>
            <div className="block">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Pied-de-page personnalisé (4-5 lignes, mise en forme libre)</div>
              <RichTextEditor
                value={v.footer_extra_line || ""}
                onChange={(html)=>setV({...v, footer_extra_line: html})}
                placeholder="Rédigez votre pied-de-page sur plusieurs lignes… Police, taille, couleur, gras, italique, alignement, listes — tout est modifiable."
                minHeight={140}
              />
              <div className="text-[10px] text-slate-500 mt-1">S'affiche <b>tout en bas du pied-de-page</b> (sous la mention CSI), propre à ce modèle. Laisser vide pour désactiver.</div>
            </div>
            </div>
            )}
            <div className="grid grid-cols-4 gap-3 flex-1 min-h-0">
              <div className="col-span-3 flex flex-col min-h-0 tpl-content-section">
                <div className="tpl-content-label text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 bg-white z-20 pt-0.5 pb-1">Contenu du modèle</div>
                <RichTextEditor
                  value={v.body}
                  onChange={(html)=>setV({...v, body: html})}
                  placeholder="Rédigez votre modèle de document… Vous pouvez modifier la police, la taille, la couleur, l'alignement, insérer des listes, etc."
                  minHeight={360}
                  onApi={(api)=>{ rteApi.current = api; }}
                />
                <div className="text-[10px] text-slate-500 mt-1">Modifiez à droite pour voir l'aperçu PDF en temps réel. Les placeholders ci-contre s'insèrent à la position du curseur.</div>
              </div>
              <div className="col-span-1 flex flex-col min-h-0 tpl-placeholders-col">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 bg-white pt-0.5">Placeholders</div>
                <div className="space-y-1 overflow-auto flex-1 pr-1 tpl-placeholders-list">
                  {PLACEHOLDERS.map((p)=>(
                    <button key={p.code} onClick={()=>insertPlaceholder(p.code)} className="w-full text-left text-[10px] px-2 py-1.5 rounded bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300">
                      <div className="font-mono text-sky-700">{p.code}</div>
                      <div className="text-slate-500">{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : aperçu PDF en direct */}
          <div className="min-h-0 h-full">
            <DocPreviewPane
              body={v.body}
              title={v.name}
              logoSizeCm={v.logo_size_cm}
              footerExtraLine={v.footer_extra_line}
              footerExtraFontSize={v.footer_extra_font_size}
              footerExtraColor={v.footer_extra_color}
              footerExtraBold={v.footer_extra_bold}
              footerExtraItalic={v.footer_extra_italic}
              kind={v.kind}
            />
          </div>
        </div>
        {err && <div className="px-5 pb-3 text-sm text-rose-700">{err}</div>}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50">Annuler</button>
          <button data-testid="tpl-save" onClick={save} disabled={saving || !v.name || !v.body} className="px-4 py-2 text-sm font-semibold text-white rounded disabled:opacity-60" style={{backgroundColor:"var(--brand-primary)"}}>{saving?"Enregistrement…":"Enregistrer"}</button>
        </div>
      </div>
      <style>{`
        /* Contenu du modèle : l'éditeur Quill scrolle en interne, la toolbar et les placeholders restent visibles.
           Note : react-quill insère un wrapper <div class="quill"> entre .rte-wrapper et .ql-container,
           il faut donc aussi le configurer en flex-column pour que la chaîne de contraintes de hauteur fonctionne. */
        .tpl-content-section { overflow: hidden; }
        .tpl-content-section .rte-wrapper { flex: 1; min-height: 0; display: flex; flex-direction: column; }
        .tpl-content-section .rte-wrapper > .quill { flex: 1; min-height: 0; display: flex; flex-direction: column; }
        .tpl-content-section .rte-wrapper > .quill > .ql-container.ql-snow { flex: 1 1 0; min-height: 0 !important; overflow-y: auto; }
        .tpl-content-section .rte-wrapper > .quill > .ql-container .ql-editor { min-height: unset !important; height: auto; }
        .tpl-placeholders-col { overflow: hidden; }
        .tpl-placeholders-list { max-height: 100%; }
      `}</style>
    </div>
  );
}

export function GenerateDocDialog({ agent, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [logoSize, setLogoSize] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [sentInfo, setSentInfo] = useState(null); // { sign_url, email_sent, email_reason }

  useEffect(() => { http.get("/doc-templates").then(r=>{
    setTemplates(r.data);
    if (r.data[0]) { setTemplateId(r.data[0].id); setBody(textToHtmlIfNeeded(r.data[0].body)); setLogoSize(r.data[0].logo_size_cm||5); }
  }); }, []);

  const selectTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find(x=>x.id===id);
    if (t) { setBody(textToHtmlIfNeeded(t.body)); setLogoSize(t.logo_size_cm||5); }
  };

  const generate = async () => {
    setGenerating(true); setErr("");
    try {
      const t = templates.find(x=>x.id===templateId);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.id}/generate-document`, {
        method: "POST", credentials: "include",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ template_id: templateId, custom_body: body, logo_size_cm: logoSize, title_override: t?.name })
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(t?.name||"Document").replace(/ /g,"_")}_${agent.last_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) { setErr(String(e.message||e)); }
    finally { setGenerating(false); }
  };

  const sendForSignature = async () => {
    setSending(true); setErr(""); setSentInfo(null);
    try {
      const t = templates.find(x=>x.id===templateId);
      const r = await http.post(`/agents/${agent.id}/prepare-signature`, {
        template_id: templateId, custom_body: body, logo_size_cm: logoSize,
        title_override: t?.name, send_email: true,
      });
      setSentInfo(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSending(false); }
  };

  const generateFiche = async () => {
    setGenerating(true); setErr("");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.id}/fiche.pdf?logo_size_cm=${logoSize}`, {credentials:"include"});
      if (!resp.ok) throw new Error("Erreur génération fiche");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Fiche_${agent.last_name}_${agent.first_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) { setErr(String(e.message||e)); }
    finally { setGenerating(false); }
  };

  const copyLink = async () => {
    if (!sentInfo?.sign_url) return;
    try { await navigator.clipboard.writeText(sentInfo.sign_url); } catch (_e) { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-3">
      <div className={`bg-white rounded-md w-full ${sentInfo ? "max-w-4xl" : "max-w-[1500px]"} border border-slate-200 max-h-[94vh] overflow-hidden flex flex-col`} onClick={(e)=>e.stopPropagation()} data-testid="gen-doc-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Générer un document — {agent.first_name} {agent.last_name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 flex-1 overflow-hidden">
          {sentInfo ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4" data-testid="sent-info">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-2">
                <CheckCircle2 className="h-5 w-5" />
                {sentInfo.email_sent
                  ? `Lien envoyé par email à ${agent.email || "l'agent"}`
                  : "Lien créé (l'email n'a pas pu être envoyé — vous pouvez le copier ci-dessous)"}
              </div>
              {!sentInfo.email_sent && sentInfo.email_reason && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                  Raison : {sentInfo.email_reason}
                </div>
              )}
              <label className="block">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Lien de signature</div>
                <div className="flex gap-2">
                  <input readOnly value={sentInfo.sign_url} data-testid="sign-url-input" className="flex-1 border border-slate-300 rounded px-3 py-2 text-xs font-mono bg-white" />
                  <button onClick={copyLink} data-testid="copy-sign-url" className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50">
                    <Copy className="h-4 w-4" /> Copier
                  </button>
                  <a href={sentInfo.sign_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50">
                    <LinkIcon className="h-4 w-4" /> Ouvrir
                  </a>
                </div>
              </label>
              <div className="text-xs text-slate-600 mt-2">Vous pouvez retrouver et suivre ce document depuis l'onglet <b>Documents agents</b>. Un rappel peut être renvoyé à tout moment.</div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-4 h-full">
              {/* Colonne gauche : édition */}
              <div className="flex flex-col min-h-0 overflow-auto pr-1 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <label className="col-span-2 block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Choisir un modèle</div>
                    <select data-testid="gen-tpl-select" className="w-full border border-slate-300 rounded px-3 py-2 text-sm" value={templateId} onChange={(e)=>selectTemplate(e.target.value)}>
                      {templates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Logo (cm)</div>
                    <input type="number" step="0.5" min="1" max="10" className="w-full border border-slate-300 rounded px-3 py-2 text-sm" value={logoSize} onChange={(e)=>setLogoSize(parseFloat(e.target.value)||5)} />
                  </label>
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Contenu — éditez avant de générer</div>
                  <RichTextEditor
                    value={body}
                    onChange={setBody}
                    minHeight={320}
                  />
                  <div className="text-[10px] text-slate-500 mt-1">L'aperçu à droite se met à jour en temps réel avec les données réelles de l'agent.</div>
                </div>
                {!agent.email && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                    ⚠️ Cet agent n'a pas d'email renseigné. Vous pouvez toujours télécharger le PDF, mais l'envoi pour signature nécessite un email.
                  </div>
                )}
                {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
              </div>

              {/* Colonne droite : aperçu PDF live avec l'agent réel */}
              <div className="min-h-0 h-full">
                {(() => {
                  const t = templates.find(x=>x.id===templateId);
                  return (
                    <DocPreviewPane
                      body={body}
                      title={t?.name || "Document"}
                      logoSizeCm={logoSize}
                      footerExtraLine={t?.footer_extra_line || ""}
                      footerExtraFontSize={t?.footer_extra_font_size || 7}
                      footerExtraColor={t?.footer_extra_color || "#475569"}
                      footerExtraBold={!!t?.footer_extra_bold}
                      footerExtraItalic={t?.footer_extra_italic !== false}
                      kind={t?.kind || "generic"}
                      defaultAgentId={agent.id}
                    />
                  );
                })()}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-between gap-2 flex-wrap">
          {sentInfo ? (
            <button onClick={onClose} data-testid="close-sent" className="ml-auto px-4 py-2 text-sm font-semibold text-white rounded" style={{backgroundColor:"var(--brand-primary)"}}>Fermer</button>
          ) : (
            <>
              <button onClick={generateFiche} disabled={generating||sending} className="px-4 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">📇 Générer fiche agent</button>
              <div className="flex gap-2 flex-wrap">
                <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50">Annuler</button>
                <button data-testid="gen-download" onClick={generate} disabled={generating || sending || !body} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">
                  <Download className="h-4 w-4" /> {generating?"Génération…":"Télécharger PDF"}
                </button>
                <button data-testid="gen-send-signature" onClick={sendForSignature} disabled={generating || sending || !body || !agent.email} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded disabled:opacity-60" style={{backgroundColor:"var(--brand-primary)"}}>
                  <Send className="h-4 w-4" /> {sending?"Envoi…":"Envoyer pour signature"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DocTemplates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const r = await http.get("/doc-templates"); setItems(r.data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!askDelete("Supprimer ce modèle ?")) return;
    try { await http.delete(`/doc-templates/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const duplicate = async (t) => {
    try { await http.post("/doc-templates", { name: t.name + " (copie)", kind: t.kind, body: t.body, logo_size_cm: t.logo_size_cm }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Modèles de documents</h1>
          <p className="text-sm text-slate-600">Créez vos modèles de contrat, attestation, avenant… avec des placeholders <code className="text-xs bg-slate-100 px-1 rounded">{"{{agent.first_name}}"}</code> automatiquement remplis lors de la génération.</p>
        </div>
        <button data-testid="tpl-new" onClick={()=>setEditing({name:"",kind:"contract",body:"",logo_size_cm:5})} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold text-white rounded" style={{backgroundColor:"var(--brand-primary)"}}>
          <Plus className="h-4 w-4" /> Nouveau modèle
        </button>
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="py-2 px-3 text-left">Nom</th><th className="py-2 px-3 text-left">Type</th><th className="py-2 px-3 text-left">Logo</th><th className="py-2 px-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length===0 ? (<tr><td colSpan={4} className="py-6 text-center text-slate-500">Aucun modèle. Créez-en un pour commencer.</td></tr>) :
              items.map(t=>(
                <tr key={t.id} className="hover:bg-slate-50" data-testid={`tpl-row-${t.id}`}>
                  <td className="py-2 px-3 font-medium"><FileText className="inline h-4 w-4 text-slate-400 mr-2"/>{t.name}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{KIND_LABELS[t.kind]||t.kind}</td>
                  <td className="py-2 px-3 text-xs">{t.logo_size_cm||5} cm</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={()=>duplicate(t)} title="Dupliquer" className="p-1.5 hover:bg-slate-100 rounded"><Copy className="h-4 w-4 text-slate-600"/></button>
                    <button data-testid={`tpl-edit-${t.id}`} onClick={()=>setEditing(t)} title="Modifier" className="p-1.5 hover:bg-slate-100 rounded"><Pencil className="h-4 w-4 text-sky-600"/></button>
                    <button onClick={()=>del(t.id)} title={delTitle()} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4 text-rose-600"/></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {editing && <TemplateEditor template={editing} onClose={()=>setEditing(null)} onSaved={()=>{ setEditing(null); load(); }} />}
    </div>
  );
}
